import { useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { captureException } from '@/lib/sentry';

interface RouteErrorProps {
  error: Error;
  reset: () => void;
}

export function RouteError({ error, reset }: RouteErrorProps) {
  const router = useRouter();

  useEffect(() => {
    captureException(error, { context: 'route-error', path: router.state.location.pathname });
  }, [error, router.state.location.pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="max-w-md w-full">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Page failed to load</AlertTitle>
          <AlertDescription className="mt-2 space-y-2">
            <p>Something went wrong loading this page. This has been logged.</p>
            {import.meta.env.DEV && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sm font-medium">Error details</summary>
                <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                  {error.message}
                </pre>
              </details>
            )}
            <div className="flex gap-2 mt-4">
              <Button onClick={() => router.navigate({ to: '/' })} variant="outline" size="sm">
                Go Home
              </Button>
              <Button onClick={reset} variant="default" size="sm" className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}
